// i18n-ignore-file — translation resources of the "cineforge" namespace
// CineForge strings, registered as the "cineforge" i18next namespace so upstream locale files stay untouched.
import i18n from "../i18n";
import { rebrandI18n } from "./branding";
import { applyTextOverrides } from "./textOverrides";

const fr = {
  nav: { prompts: "Prompts" },
  page: {
    title: "Prompts",
    subtitle: "Tous les prompts envoyés aux modèles, modifiables pour toute l'équipe ou pour un projet.",
    search: "Rechercher un prompt, une clé ou un texte…",
    viewAs: "Voir pour",
    allProjects: "Tous les projets (global)",
    showButtons: "Boutons « modifier le prompt » dans l'app",
    export: "Exporter",
    import: "Importer",
    imported_one: "{{count}} override importé", imported_other: "{{count}} overrides importés",
    exportReady: "Export téléchargé",
    readonly: "Lecture seule",
    noneMatch: "Aucun prompt ne correspond à ces filtres.",
    pick: "Choisissez un prompt dans la liste.",
    editable: "modifiables",
    sections: "Sections",
  },
  filter: { all: "Tous", overridden: "Modifiés", upstream: "Changés upstream", projects: "Overrides projet", drafts: "Brouillons", readonly: "Lecture seule" },
  chip: {
    default: "Défaut", global: "Global v{{v}}", project: "Projet v{{v}}", projects_one: "{{count}} projet", projects_other: "{{count}} projets",
    upstream: "Changé upstream", draft_one: "{{count}} brouillon", draft_other: "{{count}} brouillons", template: "Template", fragment: "Fragment", schema: "Schéma JSON",
    readonly: "Lecture seule", computed: "Assemblé dans le code",
  },
  editor: {
    tabDefault: "Défaut (code)", tabGlobal: "Global", tabProject: "Projet",
    usedBy: "Utilisé par", model: "Modèle", builtFrom: "Construit à partir de", feeds: "Alimente",
    note: "Note (obligatoire) : ce qui change et pourquoi",
    saveDraft: "Enregistrer en brouillon", savePublish: "Enregistrer et publier",
    published: "Version v{{v}} publiée", draftSaved: "Brouillon v{{v}} enregistré",
    reset: "Revenir au niveau inférieur", resetDone: "Override retiré",
    resetConfirm: "Confirmer le retour au niveau inférieur",
    compare: "Comparer", hideCompare: "Masquer la comparaison",
    compareWithDefault: "Différences avec le défaut", compareWithGlobal: "Différences avec le global",
    history: "Historique", restore: "Restaurer", publish: "Publier", restored: "Version restaurée",
    noOverride: "Pas d'override : le texte par défaut est utilisé. Modifiez-le ci-dessous pour en créer un.",
    inherits: "Ce projet utilise le global v{{v}}.",
    inheritsDefault: "Ce projet utilise le texte par défaut.",
    placeholders: "Placeholders", missing: "manquant : {{name}}", unknown: "inconnu : {{name}}",
    allPresent: "{{n}}/{{total}} présents",
    upstreamTitle: "Le texte par défaut a changé dans le code depuis cette version.",
    upstreamBody: "Comparez l'ancien et le nouveau défaut, puis reportez ce qui vous intéresse dans votre version.",
    upstreamShow: "Voir les changements upstream", upstreamHide: "Masquer",
    oldDefault: "Ancien défaut", newDefault: "Nouveau défaut",
    globalMoved: "Le global est en v{{now}}, ce projet est basé sur la v{{base}}.",
    readonlyNote: "Ce prompt est listé pour référence. Il n'est pas encore branché sur le registre, donc il ne peut pas être modifié ici.",
    copy: "Copier", copied: "Copié",
    usage: "Utilisations récentes", noUsage: "Pas encore d'utilisation enregistrée.",
    unsaved: "Modifications non enregistrées",
    chars: "{{n}} caractères",
    errors: "Impossible d'enregistrer",
    by: "par {{name}}",
    refineExample: "Ex : plus concis, insiste sur la continuité des personnages, garde le format JSON…",
    refineShorter: "La version proposée est nettement plus courte que l'originale : vérifiez qu'elle n'est pas tronquée (Comparer).",
    unchangedLines_one: "… {{count}} ligne inchangée …", unchangedLines_other: "… {{count}} lignes inchangées …",
    noDifferences: "Aucune différence.",
    chipLive: "en ligne", chipDraft: "brouillon", chipReset: "réinitialisé", background: "tâche de fond",
    placeholderError: {
      unclosed: "« { » non fermée au caractère {{at}}", nested: "« { » imbriquée au caractère {{at}}",
      empty: "Placeholder vide « {} » au caractère {{at}}", single: "« } » isolée au caractère {{at}}",
    },
  },
  button: {
    title: "Modifier les prompts de cette action", sheetTitle: "Prompts utilisés par « {{label}} »",
    back: "Retour à la liste", openPage: "Ouvrir dans la page Prompts", scopeProject: "Ce projet", scopeGlobal: "Global",
  },
  project: {
    title: "Prompts du projet", inputs: "Entrées du projet déjà utilisées par l'IA",
    creativeVision: "Vision créative", directorVision: "Vision du réalisateur", genre: "Genre", type: "Type",
    overrides: "Overrides de ce projet", none: "Aucun override : ce projet utilise les prompts globaux.",
    add: "Ajouter un override", copyFrom: "Copier les overrides d'un autre projet", copy: "Copier",
    copied_one: "{{count}} override copié", copied_other: "{{count}} overrides copiés", runs: "Dernières générations et versions utilisées",
    noRuns: "Aucune génération enregistrée pour ce projet.", badge_one: "{{count}} prompt personnalisé", badge_other: "{{count}} prompts personnalisés",
    edit: "Modifier",
    empty: "—",
  },
  layer: { default: "défaut", global: "global v{{v}}", project: "projet v{{v}}" },
  providers: {
    title: "Modèles et fournisseurs",
    desc: "Ce que Pipeline a le droit d'utiliser. Un fournisseur refusé ou sans droit aux médias de personnes bloque l'envoi avant toute dépense, avec un message clair.",
    provider: "Fournisseur", location: "Traitement", dpa: "Contrat RGPD", allowed: "Autorisé",
    personalMedia: "Médias de personnes", personalMediaHint: "Photos, vidéos ou voix envoyées par Pipeline (visages, voix d'acteurs).",
    yes: "oui", no: "non", unknown: "inconnu",
    save: "Enregistrer", saved: "Réglages enregistrés", failed: "Enregistrement impossible",
    lastChange: "Dernière modification : {{who}}",
    aria: "{{name}} : {{what}}",
    tab: "APIs",
    modelRule: "Sous chaque fournisseur, ses modèles. L'interrupteur d'un modèle vaut pour tous ses fournisseurs ; un modèle part seulement si lui ET un de ses fournisseurs sont autorisés.",
    modelState: { on: "Actif", off: "Modèle coupé", via: "Fournisseur coupé — reste disponible via {{via}} (selon le rendu)", providerOff: "Fournisseur coupé — indisponible" },
    keys: {
      key: "Clé API", notInPolicy: "Service sans réglage fournisseur (clé seulement)",
      test: "Tester les clés (gratuit)", testFailed: "Test des clés impossible",
      testedNow: "Testé à l'instant", notTested: "Présence seulement — pas encore testé",
      status: { valid: "Valide", invalid: "Refusée", missing: "À brancher", present: "Présente", error: "Non vérifiable" },
    },
  },
  seedanceRoute: {
    title: "Rendus Seedance 2.5",
    desc: "Où partent les rendus Seedance 2.5 de Pipeline. La 4K, l'extension et les personnages entraînés restent sur MuAPI.",
    muapi: "MuAPI", byteplus: "BytePlus (notre compte)",
    current: "Actuellement : {{route}} ({{source}})",
    source_admin: "réglé ici", source_env: "réglage serveur", source_default: "par défaut",
    notReady: "BytePlus n'est pas configuré sur ce serveur.",
    switched: "Rendus Seedance : {{route}}",
    failed: "Bascule impossible",
  },
  models: {
    title: "Modèles IA par étape",
    desc: "Le modèle utilisé à chaque étape du pipeline. Chaque nouveau choix est testé par un petit appel avant d'être enregistré ; il s'applique en 30 secondes, sans redéploiement.",
    group_claude: "Texte et jugement (Claude)", group_gemini: "Analyse d'images et de vidéos (Gemini)",
    role_claude_script: "Lecture et relecture du script", role_claude_bible: "Bible",
    role_claude_screenplay: "Scénario, découpage, devis", role_claude_locks: "Serrures et juges",
    role_claude_prompts: "Prompts et assistants prompts", role_claude_assistants: "Assistants (Rachel, chats)",
    role_claude: "Tout le reste", role_gemini_fast: "Gemini rapide (classement, visages)",
    role_gemini_pro: "Gemini Pro (analyse vidéo, vérifications)",
    onlyOne: "seul modèle qualifié pour l'instant",
    optionDefault: "{{label}} (par défaut)",
    test: "Tester", testing: "Test…", testOk: "Répond ({{ms}} ms)", testFail: "Ne répond pas : {{error}}",
    save: "Enregistrer", saving: "Enregistrement…", saved: "Modèles enregistrés", failed: "Enregistrement impossible",
    updated: "Dernier changement : {{who}}, {{when}}",
  },
};

const en = {
  nav: { prompts: "Prompts" },
  page: {
    title: "Prompts",
    subtitle: "Every prompt sent to the models, editable for the whole team or for one project.",
    search: "Search prompts, keys or text…",
    viewAs: "View as",
    allProjects: "All projects (global)",
    showButtons: "\"Edit prompt\" buttons in the app",
    export: "Export",
    import: "Import",
    imported_one: "{{count}} override imported", imported_other: "{{count}} overrides imported",
    exportReady: "Export downloaded",
    readonly: "Read-only",
    noneMatch: "No prompt matches these filters.",
    pick: "Pick a prompt from the list.",
    editable: "editable",
    sections: "Sections",
  },
  filter: { all: "All", overridden: "Overridden", upstream: "Upstream changed", projects: "Project overrides", drafts: "Drafts", readonly: "Read-only" },
  chip: {
    default: "Default", global: "Global v{{v}}", project: "Project v{{v}}", projects_one: "{{count}} project", projects_other: "{{count}} projects",
    upstream: "Upstream changed", draft_one: "{{count}} draft", draft_other: "{{count}} drafts", template: "Template", fragment: "Fragment", schema: "JSON schema",
    readonly: "Read-only", computed: "Assembled in code",
  },
  editor: {
    tabDefault: "Default (code)", tabGlobal: "Global", tabProject: "Project",
    usedBy: "Used by", model: "Model", builtFrom: "Built from", feeds: "Feeds",
    note: "Note (required): what changes and why",
    saveDraft: "Save as draft", savePublish: "Save and publish",
    published: "Version v{{v}} published", draftSaved: "Draft v{{v}} saved",
    reset: "Go back to the layer below", resetDone: "Override removed",
    resetConfirm: "Confirm going back to the layer below",
    compare: "Compare", hideCompare: "Hide comparison",
    compareWithDefault: "Differences from the default", compareWithGlobal: "Differences from the global version",
    history: "History", restore: "Restore", publish: "Publish", restored: "Version restored",
    noOverride: "No override: the default text is used. Edit it below to create one.",
    inherits: "This project uses global v{{v}}.",
    inheritsDefault: "This project uses the default text.",
    placeholders: "Placeholders", missing: "missing: {{name}}", unknown: "unknown: {{name}}",
    allPresent: "{{n}}/{{total}} present",
    upstreamTitle: "The default text changed in the code since this version.",
    upstreamBody: "Compare the old and new default, then carry over what you want into your version.",
    upstreamShow: "Show upstream changes", upstreamHide: "Hide",
    oldDefault: "Old default", newDefault: "New default",
    globalMoved: "Global is now v{{now}}; this project is based on v{{base}}.",
    readonlyNote: "This prompt is listed for reference. It isn't wired to the registry yet, so it can't be edited here.",
    copy: "Copy", copied: "Copied",
    usage: "Recent uses", noUsage: "No recorded use yet.",
    unsaved: "Unsaved changes",
    chars: "{{n}} characters",
    errors: "Can't save",
    by: "by {{name}}",
    refineExample: "E.g.: more concise, stress character continuity, keep the JSON format…",
    refineShorter: "The proposed version is much shorter than the original: check it is not cut off (Compare).",
    unchangedLines_one: "… {{count}} unchanged line …", unchangedLines_other: "… {{count}} unchanged lines …",
    noDifferences: "No differences.",
    chipLive: "live", chipDraft: "draft", chipReset: "reset", background: "background",
    placeholderError: {
      unclosed: "Unclosed \"{\" at character {{at}}", nested: "Nested \"{\" at character {{at}}",
      empty: "Empty placeholder \"{}\" at character {{at}}", single: "Single \"}\" at character {{at}}",
    },
  },
  button: {
    title: "Edit the prompts for this action", sheetTitle: "Prompts used by \"{{label}}\"",
    back: "Back to the list", openPage: "Open in the Prompts page", scopeProject: "This project", scopeGlobal: "Global",
  },
  project: {
    title: "Project prompts", inputs: "Project inputs already used by the AI",
    creativeVision: "Creative vision", directorVision: "Director's vision", genre: "Genre", type: "Type",
    overrides: "Overrides in this project", none: "No override: this project uses the global prompts.",
    add: "Add an override", copyFrom: "Copy overrides from another project", copy: "Copy",
    copied_one: "{{count}} override copied", copied_other: "{{count}} overrides copied", runs: "Recent generations and the prompt versions they used",
    noRuns: "No generation recorded for this project yet.", badge_one: "{{count}} custom prompt", badge_other: "{{count}} custom prompts",
    edit: "Edit",
    empty: "—",
  },
  layer: { default: "default", global: "global v{{v}}", project: "project v{{v}}" },
  providers: {
    title: "Models & providers",
    desc: "What Pipeline may use. A blocked provider, or one without the right to personal media, stops the call before any spend, with a clear message.",
    provider: "Provider", location: "Processing", dpa: "GDPR contract", allowed: "Allowed",
    personalMedia: "Personal media", personalMediaHint: "Photos, videos or voices Pipeline sends (actors' faces, voices).",
    yes: "yes", no: "no", unknown: "unknown",
    save: "Save", saved: "Settings saved", failed: "Could not save",
    lastChange: "Last change: {{who}}",
    aria: "{{name}}: {{what}}",
    tab: "APIs",
    modelRule: "Under each provider, its models. A model's switch applies to all its providers; a model is sent only if it AND one of its providers are allowed.",
    modelState: { on: "Active", off: "Model off", via: "Provider off — still available via {{via}} (depending on the render)", providerOff: "Provider off — unavailable" },
    keys: {
      key: "API key", notInPolicy: "Service without a provider setting (key only)",
      test: "Test keys (free)", testFailed: "Could not test the keys",
      testedNow: "Tested just now", notTested: "Presence only, not tested yet",
      status: { valid: "Valid", invalid: "Rejected", missing: "To connect", present: "Set", error: "Could not check" },
    },
  },
  seedanceRoute: {
    title: "Seedance 2.5 renders",
    desc: "Where Pipeline's Seedance 2.5 renders go. 4K, extend and trained characters stay on MuAPI.",
    muapi: "MuAPI", byteplus: "BytePlus (our account)",
    current: "Currently: {{route}} ({{source}})",
    source_admin: "set here", source_env: "server setting", source_default: "default",
    notReady: "BytePlus is not configured on this server.",
    switched: "Seedance renders: {{route}}",
    failed: "Could not switch",
  },
  models: {
    title: "AI models per step",
    desc: "The model used at each step of the pipeline. Each new choice is tested with a tiny call before it is saved; it applies within 30 seconds, without a redeploy.",
    group_claude: "Text and judgement (Claude)", group_gemini: "Image and video analysis (Gemini)",
    role_claude_script: "Script reading and review", role_claude_bible: "Bible",
    role_claude_screenplay: "Screenplay, shot breakdown, quotes", role_claude_locks: "Locks and judges",
    role_claude_prompts: "Prompts and prompt assistants", role_claude_assistants: "Assistants (Rachel, chats)",
    role_claude: "Everything else", role_gemini_fast: "Fast Gemini (sorting, faces)",
    role_gemini_pro: "Gemini Pro (video analysis, checks)",
    onlyOne: "only qualified model for now",
    optionDefault: "{{label}} (default)",
    test: "Test", testing: "Testing…", testOk: "Answers ({{ms}} ms)", testFail: "No answer: {{error}}",
    save: "Save", saving: "Saving…", saved: "Models saved", failed: "Could not save",
    updated: "Last change: {{who}}, {{when}}",
  },
};

i18n.addResourceBundle("fr", "cineforge", fr, true, true);
i18n.addResourceBundle("en", "cineforge", en, true, true);

export const NS = "cineforge";

// Rebrand upstream names (Moodwalkrs / Corpus) in every loaded string — see ./branding.js.
rebrandI18n(i18n);
// CineForge wording (films, not ads) — see ./textOverrides.js.
applyTextOverrides(i18n);
